<!--
  TrialBalanceEditDrawer — link tasks to one trial-balance row on the paginated screen.
  Used only by TrialBalancePaginated. The fallback TrialBalance.vue keeps its own drawer.
-->
<template>
  <div>
    <lb-drawer
      :value="value"
      heading="Edit Account Details"
      :loading="saving"
      width="600"
      data-testid="financialclose-project-trialbalance-paginated__tasklistdrawer"
      @input="$emit('input', $event)"
    >
      <template v-slot:body>
        <v-row data-testid="financialclose-project-trialbalance-paginated__tasklistdrawer-row">
          <v-col cols="3">
            <span class="grey--text">Account No.</span>
          </v-col>
          <v-col cols="9">
            <span class="caccounting--text caccounting-background pa-1 radius-mini">{{ item.glcode }}</span>
          </v-col>
          <v-col cols="3">
            <span class="grey--text">Account Name</span>
          </v-col>
          <v-col cols="9">
            <span>{{ item.name }}</span>
          </v-col>
          <v-col cols="3">
            <span class="grey--text">Type</span>
          </v-col>
          <v-col cols="9">
            <span>{{ item.type }}</span>
          </v-col>
          <v-col cols="3">
            <span class="grey--text">{{ locationLabel }}</span>
          </v-col>
          <v-col cols="9">
            <span class="caccounting--text caccounting-background pa-1 radius-mini">{{ locationNames[0] }}</span>
          </v-col>
          <v-col cols="3">
            <span class="grey--text">Category</span>
          </v-col>
          <v-col cols="9">
            <span>{{ item.category }}</span>
          </v-col>
          <v-col cols="3">
            <span class="grey--text">Sub Category</span>
          </v-col>
          <v-col cols="9">
            <span>{{ item.subcategory }}</span>
          </v-col>
          <v-col cols="3">
            <span class="grey--text">Opening Amount</span>
          </v-col>
          <v-col cols="9">
            <span>{{ $nova.formatNumber(item.opening_amount) }}</span>
          </v-col>
          <v-col cols="3">
            <span class="grey--text">Transaction</span>
          </v-col>
          <v-col cols="9">
            <span>{{ $nova.formatNumber(item.transaction) }}</span>
          </v-col>
          <v-col cols="3">
            <span class="grey--text">Journal Entries</span>
          </v-col>
          <v-col cols="9">
            <span>{{ $nova.formatNumber(item.audit_entries) }}</span>
          </v-col>
          <v-col cols="3">
            <span class="grey--text">Closing Amount</span>
          </v-col>
          <v-col cols="9">
            <span>{{ $nova.formatNumber(item.closing_amount) }}</span>
          </v-col>
        </v-row>
        <div class="d-flex align-center justify-end mb-2 mt-2">
          <v-btn
            class="shadow-off"
            text
            :disabled="mutationsDisabled"
            data-testid="financialclose-project-trialbalance-paginated__tasklistdrawer-addtask-btn"
            @click="openSelect"
          >
            <v-icon class="mr-1">mdi-plus</v-icon>
            <span>Add Task</span>
          </v-btn>
        </div>
        <v-simple-table
          v-if="references.length"
          dense
          class="simple-table border-on radius-small"
          data-testid="financialclose-project-trialbalance-paginated__tasklistdrawer-table"
        >
          <thead class="tableHeaderBg">
            <tr>
              <th class="tableHeaderBg" scope="col">Reference</th>
              <th class="tableHeaderBg" scope="col">Description</th>
              <th class="tableHeaderBg" scope="col">Category</th>
              <th class="tableHeaderBg" scope="col">Remove</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in references"
              :key="row._id || index"
              :data-testid="`financialclose-project-trialbalance-paginated__tasklistdrawer-row--${index}`"
            >
              <td>{{ row.ref }}</td>
              <td>{{ row.description }}</td>
              <td>{{ item.category }}</td>
              <td>
                <v-btn
                  icon
                  :disabled="mutationsDisabled"
                  :data-testid="`financialclose-project-trialbalance-paginated__tasklistdrawer-remove-btn--${index}`"
                  @click="removeRef(index)"
                >
                  <v-icon color="error">mdi-delete</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
        <div v-else class="d-flex grey--text py-4 text-center">
          <span class="font-size-lg">No task linked</span>
        </div>
      </template>
      <template v-slot:actions>
        <v-spacer />
        <v-btn
          class="ml-2"
          color="primary"
          :disabled="mutationsDisabled"
          data-testid="financialclose-project-trialbalance-paginated__tasklistdrawer-update-btn"
          @click="saveLinks"
        >
          <span>Update</span>
        </v-btn>
      </template>
    </lb-drawer>

    <lb-drawer
      v-model="selectOpen"
      heading="Select Task(s)"
      width="600"
      :loading="selectLoading"
      data-testid="financialclose-project-trialbalance-paginated__selectgldrawer"
    >
      <template v-slot:body>
        <div
          class="trial-balance-edit__select-drawer"
          data-testid="financialclose-project-trialbalance-paginated__selectgldrawer-body"
        >
          <p class="trial-balance-edit__select-label trial-balance-edit__select-label--required">
            Tasklist
          </p>
          <SearchableMultiSelectDropdown
            :value="selectedTaskIds"
            :items="locationtaskList"
            variant="field"
            :show-chips="true"
            placeholder="Select tasklist"
            item-text="displayText"
            item-value="_id"
            search-placeholder="Search tasklist"
            select-all-row-label="All tasks"
            empty-message="No tasks available to select"
            max-activator-width="100%"
            :attach="false"
            :use-fixed-menu-position="true"
            menu-content-class="trial-balance-edit__task-menu"
            :disabled="selectLoading || selectFailed"
            data-testid-prefix="financialclose-project-trialbalance-paginated__selectgldrawer"
            @input="selectedTaskIds = $event"
          />
        </div>
      </template>
      <template v-slot:actions>
        <div class="trial-balance-edit__select-footer">
          <span class="trial-balance-edit__select-required">*Required</span>
          <v-spacer />
          <v-btn
            outlined
            class="secondaryBtn text-none mr-2"
            data-testid="financialclose-project-trialbalance-paginated__selectgldrawer-cancel-btn"
            @click="selectOpen = false"
          >
            <span>Cancel</span>
          </v-btn>
          <v-btn
            color="primary"
            class="white--text text-none"
            :disabled="!canAddSelectedTasks"
            data-testid="financialclose-project-trialbalance-paginated__selectgldrawer-add-btn"
            @click="addSelected"
          >
            <span>Add</span>
          </v-btn>
        </div>
      </template>
    </lb-drawer>
  </div>
</template>

<script>
import './TrialBalanceEditDrawer.scss';
import { mapActions, mapGetters } from 'vuex';
import { SearchableMultiSelectDropdown } from '../../../../components';

export default {
  name: 'TrialBalanceEditDrawer',

  components: { SearchableMultiSelectDropdown },

  props: {
    value: { type: Boolean, default: false },
    item: { type: Object, default: () => ({}) },
    projectId: { type: String, default: '' },
    locationNames: { type: Array, default: () => [] },
    locationLabel: { type: String, default: 'Location' },
    mutationsDisabled: { type: Boolean, default: false },
  },

  data() {
    return {
      references: [],
      selectOpen: false,
      selectLoading: false,
      selectFailed: false,
      selectRequestId: 0,
      selectedTaskIds: [],
      saving: false,
    };
  },

  computed: {
    ...mapGetters('trialBalance', ['locationtaskList']),
    canAddSelectedTasks() {
      return Array.isArray(this.selectedTaskIds) && this.selectedTaskIds.length > 0;
    },
  },

  watch: {
    value(open) {
      if (open) this.syncReferences();
    },
    item() {
      if (this.value) this.syncReferences();
    },
  },

  beforeDestroy() {
    this.references = [];
    this.selectedTaskIds = [];
    this.selectOpen = false;
    this.selectFailed = false;
    this.selectRequestId += 1;
    this.saving = false;
  },

  methods: {
    ...mapActions('trialBalance', {
      fetchLocationTasklistAction: 'fetchLocationTasklist',
      editTrialBalanceAction: 'editTrialBalance',
    }),

    syncReferences() {
      const list = Array.isArray(this.item && this.item.reference_detail) ? this.item.reference_detail : [];
      this.references = list.map((row) => ({
        _id: row && row._id,
        ref: row && row.ref,
        description: row && row.description,
      }));
    },

    /**
     * Location task list is keyed by the row's location id. The displayed name is not that id.
     * An empty location, including an empty array, uses the same fallback id the old screen sends.
     */
    locationIdForTasks(location) {
      if (Array.isArray(location)) {
        const first = location.find((id) => id != null && String(id) !== '');
        return first || 'axyz';
      }
      return location || 'axyz';
    },

    async openSelect() {
      if (this.mutationsDisabled) return;
      const requestId = ++this.selectRequestId;
      this.selectOpen = true;
      this.selectLoading = true;
      this.selectFailed = false;
      const locationId = this.locationIdForTasks(this.item && this.item.location);
      let loaded = false;
      try {
        loaded = await this.fetchLocationTasklistAction({ id: this.projectId, locationid: locationId });
      } catch (err) {
        loaded = false;
        console.error('[TrialBalanceEditDrawer]:', err);
      } finally {
        if (requestId === this.selectRequestId) {
          this.selectLoading = false;
        }
      }
      if (requestId !== this.selectRequestId) return;
      if (!this.selectOpen) return;
      if (!loaded) {
        this.selectFailed = true;
        this.selectedTaskIds = [];
        return;
      }
      this.selectedTaskIds = this.references.map((row) => row && row._id).filter(Boolean);
    },

    /**
     * Links already on the row stay when this location's task list does not include them.
     * Replacing the list with only the fetched options would drop those links on Update.
     */
    addSelected() {
      const selected = new Set(this.selectedTaskIds);
      const fetchedIds = new Set(
        (this.locationtaskList || []).map((task) => task && task._id).filter(Boolean),
      );
      const kept = (this.references || []).filter((row) => row && row._id && !fetchedIds.has(row._id));
      const added = (this.locationtaskList || [])
        .filter((task) => task && selected.has(task._id))
        .map((task) => ({
          _id: task._id,
          ref: task.ref,
          description: task.description,
        }));
      this.references = kept.concat(added);
      this.selectOpen = false;
    },

    removeRef(index) {
      if (this.mutationsDisabled) return;
      this.references.splice(index, 1);
    },

    async saveLinks() {
      if (this.mutationsDisabled || !this.item || !this.item._id) return;
      this.saving = true;
      try {
        const reference = this.references.map((row) => row._id).filter(Boolean);
        const success = await this.editTrialBalanceAction({
          projectId: this.projectId,
          trialId: this.item._id,
          data: { reference },
        });
        if (success) {
          this.$emit('input', false);
          this.$emit('saved');
        }
      } catch (err) {
        console.error('[TrialBalanceEditDrawer]:', err);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
